import { events, EVT } from '../utils/events.js';

/** Read UI design tokens for canvas rendering */
function readTheme() {
  const cs = getComputedStyle(document.documentElement);
  const v = (name, fallback) => (cs.getPropertyValue(name) || '').trim() || fallback;
  return {
    bg:      v('--bg', '#04060f'),
    text:    v('--text', '#e6eaff'),
    accent:  v('--accent', '#7aa2ff'),
    success: v('--success', '#6fd9a0'),
    mono:    v('--font-mono', 'monospace'),
  };
}

/** '#rrggbb' → 'rgba(r,g,b,a)' */
function rgba(hex, a) {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const n = parseInt(full, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

/**
 * Renders a 2D spore-like galaxy overview on the #galaxy-map-canvas.
 * Shows surrounding star systems as interactive dots.
 */
export class GalaxyMap {
  constructor(galaxyGenerator, gameState) {
    this.galaxy = galaxyGenerator;
    this.gameState = gameState;
    this.canvas = document.getElementById('galaxy-map-canvas');
    this.ctx = null;
    this.isOpen = false;

    // View state
    this.viewCenter = { x: 0, y: 0 };
    this.viewRadius = 15;  // How many grid cells are visible
    this.zoom = 1;
    this.selectedSystem = null;
    this.hoveredSystem = null;

    // Interaction
    this._dragging = false;
    this._dragStart = { x: 0, y: 0 };
    this._systems = [];

    this._bindEvents();
  }

  _bindEvents() {
    events.on(EVT.MAP_OPEN, () => this.open());
    events.on(EVT.MAP_CLOSE, () => this.close());

    this.canvas.addEventListener('mousedown', (e) => this._onMouseDown(e));
    this.canvas.addEventListener('mousemove', (e) => this._onMouseMove(e));
    this.canvas.addEventListener('mouseup', (e) => this._onMouseUp(e));
    this.canvas.addEventListener('wheel', (e) => this._onWheel(e));
    this.canvas.addEventListener('click', (e) => this._onClick(e));
  }

  open() {
    this.isOpen = true;
    this._theme = readTheme();
    this.canvas.width = this.canvas.offsetWidth * window.devicePixelRatio;
    this.canvas.height = this.canvas.offsetHeight * window.devicePixelRatio;
    this.ctx = this.canvas.getContext('2d');
    this.ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    // Center on current system
    this.viewCenter.x = this.gameState.currentSystem.gx;
    this.viewCenter.y = this.gameState.currentSystem.gy;
    this.selectedSystem = null;

    this._loadSystems();
    this.render();
  }

  close() {
    this.isOpen = false;
  }

  _loadSystems() {
    const r = Math.ceil(this.viewRadius / this.zoom);
    const cx = Math.round(this.viewCenter.x);
    const cy = Math.round(this.viewCenter.y);
    this._systems = this.galaxy.getRegion(cx, cy, r);
  }

  render() {
    if (!this.isOpen || !this.ctx) return;
    const ctx = this.ctx;
    const w = this.canvas.offsetWidth;
    const h = this.canvas.offsetHeight;
    const cx = w / 2;
    const cy = h / 2;
    const scale = (w / (this.viewRadius * 2)) * this.zoom;

    const theme = this._theme || (this._theme = readTheme());

    // Clear
    ctx.fillStyle = theme.bg;
    ctx.fillRect(0, 0, w, h);

    // Grid lines (subtle)
    ctx.strokeStyle = rgba(theme.accent, 0.06);
    ctx.lineWidth = 0.5;
    const gridStart = Math.floor(this.viewCenter.x - this.viewRadius / this.zoom);
    const gridEnd = Math.ceil(this.viewCenter.x + this.viewRadius / this.zoom);
    for (let gx = gridStart; gx <= gridEnd; gx++) {
      const screenX = cx + (gx - this.viewCenter.x) * scale;
      ctx.beginPath(); ctx.moveTo(screenX, 0); ctx.lineTo(screenX, h); ctx.stroke();
    }
    for (let gy = gridStart; gy <= gridEnd; gy++) {
      const screenY = cy + (gy - this.viewCenter.y) * scale;
      ctx.beginPath(); ctx.moveTo(0, screenY); ctx.lineTo(w, screenY); ctx.stroke();
    }

    // Draw systems
    for (const sys of this._systems) {
      const sx = cx + (sys.gx + sys.offset.x - this.viewCenter.x) * scale;
      const sy = cy + (sys.gy + sys.offset.y - this.viewCenter.y) * scale;

      // Skip if off-screen
      if (sx < -20 || sx > w + 20 || sy < -20 || sy > h + 20) continue;

      const starRadius = sys.star.size * 2 * this.zoom;
      const isSelected = this.selectedSystem && this.selectedSystem.gx === sys.gx && this.selectedSystem.gy === sys.gy;
      const isCurrent = sys.gx === this.gameState.currentSystem.gx && sys.gy === this.gameState.currentSystem.gy;

      // Star glow
      const gradient = ctx.createRadialGradient(sx, sy, 0, sx, sy, starRadius * 4);
      const color = '#' + sys.star.color.toString(16).padStart(6, '0');
      gradient.addColorStop(0, color);
      gradient.addColorStop(1, 'transparent');
      ctx.fillStyle = gradient;
      ctx.fillRect(sx - starRadius * 4, sy - starRadius * 4, starRadius * 8, starRadius * 8);

      // Star dot
      ctx.beginPath();
      ctx.arc(sx, sy, starRadius, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();

      // Singing star — a real traveler's song lives here. Pulsing ring +
      // note glyph make human planets findable at a glance.
      if (this.gameState.isSingingSystem?.(sys.gx, sys.gy)) {
        const pulse = 0.5 + Math.sin(Date.now() * 0.004 + sys.gx * 3 + sys.gy) * 0.5;
        ctx.beginPath();
        ctx.arc(sx, sy, starRadius + 4 + pulse * 3, 0, Math.PI * 2);
        ctx.strokeStyle = rgba(theme.accent, 0.35 + pulse * 0.5);
        ctx.lineWidth = 1.2;
        ctx.stroke();
        ctx.fillStyle = rgba(theme.accent, 0.8 + pulse * 0.2);
        ctx.font = `${Math.max(9, 11 * this.zoom)}px ${this._theme?.monoFont || 'monospace'}`;
        ctx.fillText('♪', sx + starRadius + 7, sy - starRadius - 3);
      }

      // Selection ring
      if (isSelected) {
        ctx.beginPath();
        ctx.arc(sx, sy, starRadius + 6, 0, Math.PI * 2);
        ctx.strokeStyle = theme.accent;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      // Current position indicator
      if (isCurrent) {
        ctx.beginPath();
        ctx.arc(sx, sy, starRadius + 10, 0, Math.PI * 2);
        ctx.strokeStyle = theme.success;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Planet dots (tiny orbits)
      if (this.zoom > 0.8) {
        sys.planets.forEach((p, i) => {
          const angle = Date.now() * 0.0001 * p.orbitSpeed + p.orbitPhase;
          const orbitR = (i + 1) * 3 * this.zoom;
          const px = sx + Math.cos(angle) * orbitR;
          const py = sy + Math.sin(angle) * orbitR;
          ctx.beginPath();
          ctx.arc(px, py, 1, 0, Math.PI * 2);
          ctx.fillStyle = rgba(theme.text, 0.5);
          ctx.fill();
        });
      }

      // Label
      if (this.zoom > 0.5) {
        ctx.fillStyle = rgba(theme.text, 0.5);
        ctx.font = `${10 * this.zoom}px ${theme.mono}`;
        ctx.textAlign = 'center';
        ctx.fillText(sys.name, sx, sy + starRadius + 12);
        // Show musical style when zoomed in enough
        if (this.zoom > 1.2 && sys.musicalStyle) {
          ctx.fillStyle = rgba(theme.text, 0.35);
          ctx.font = `${8 * this.zoom}px ${theme.mono}`;
          const tsStr = sys.timeSig ? `${sys.timeSig[0]}/${sys.timeSig[1]}` : '';
          ctx.fillText(`♪ ${sys.musicalStyle} ${tsStr}`, sx, sy + starRadius + 22);
        }
      }
    }

    // Animate
    if (this.isOpen) {
      requestAnimationFrame(() => this.render());
    }
  }

  _screenToGrid(mx, my) {
    const w = this.canvas.offsetWidth;
    const h = this.canvas.offsetHeight;
    const scale = (w / (this.viewRadius * 2)) * this.zoom;
    return {
      gx: this.viewCenter.x + (mx - w / 2) / scale,
      gy: this.viewCenter.y + (my - h / 2) / scale,
    };
  }

  _findSystemAt(mx, my) {
    const { gx, gy } = this._screenToGrid(mx, my);
    let closest = null;
    let closestDist = Infinity;
    for (const sys of this._systems) {
      const dx = sys.gx + sys.offset.x - gx;
      const dy = sys.gy + sys.offset.y - gy;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d < 0.5 && d < closestDist) {
        closest = sys;
        closestDist = d;
      }
    }
    return closest;
  }

  _onMouseDown(e) {
    this._dragging = true;
    this._dragStart = { x: e.offsetX, y: e.offsetY };
  }

  _onMouseMove(e) {
    if (this._dragging) {
      const dx = e.offsetX - this._dragStart.x;
      const dy = e.offsetY - this._dragStart.y;
      const w = this.canvas.offsetWidth;
      const scale = (w / (this.viewRadius * 2)) * this.zoom;
      this.viewCenter.x -= dx / scale;
      this.viewCenter.y -= dy / scale;
      this._dragStart = { x: e.offsetX, y: e.offsetY };
      this._loadSystems();
    }
  }

  _onMouseUp() {
    this._dragging = false;
  }

  _onClick(e) {
    const sys = this._findSystemAt(e.offsetX, e.offsetY);
    this.selectedSystem = sys;

    // Notify UI
    const uiEvent = new CustomEvent('galaxy-map-select', { detail: sys });
    document.dispatchEvent(uiEvent);

    // Update UI manager's warp target
    if (sys) {
      events.emit(EVT.NOTIFICATION, `Selected: ${sys.name}`);
    }
  }

  _onWheel(e) {
    e.preventDefault();
    this.zoom = Math.max(0.3, Math.min(3, this.zoom - e.deltaY * 0.001));
    this._loadSystems();
  }

  getSelectedSystem() {
    return this.selectedSystem;
  }
}
